// src/autodesk_usage/hooks/useCorporateMonthlyUsage.ts
// All comments in English only
import { useQuery } from "@tanstack/react-query";
import {
  getCorporateMonthlyUsage,
  type CorporateMonthlyUsageRow,
} from "../api/corporate";

export interface UseCorporateMonthlyUsageResult {
  data?: CorporateMonthlyUsageRow[];
  isLoading: boolean;
  error: unknown;
}

function normalizeCorporateNames(value?: string | string[] | null) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(
    new Set(list.map((item) => item.trim()).filter(Boolean))
  ).sort();
}

function normalizeCorporateMonthlyUsagePayload(
  payload: unknown
): CorporateMonthlyUsageRow[] {
  if (Array.isArray(payload)) {
    return payload as CorporateMonthlyUsageRow[];
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as any).items)
  ) {
    return (payload as any).items as CorporateMonthlyUsageRow[];
  }
  return [];
}

export function useCorporateMonthlyUsage(params?: {
  corporateName?: string | string[];
  month?: string;
  startMonth?: string;
  endMonth?: string;
}): UseCorporateMonthlyUsageResult {
  const corporateNames = normalizeCorporateNames(params?.corporateName);
  const corporateKey = corporateNames.join("|");
  const query = useQuery<CorporateMonthlyUsageRow[]>({
    queryKey: [
      "corporate-monthly-usage",
      corporateKey,
      params?.month,
      params?.startMonth,
      params?.endMonth,
    ],
    queryFn: async () => {
      if (!corporateNames.length) return [];
      const res = await getCorporateMonthlyUsage(
        corporateNames,
        params.month,
        params.startMonth,
        params.endMonth
      );
      return normalizeCorporateMonthlyUsagePayload(res.data);
    },
    enabled: corporateNames.length > 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
